// src/components/PaymentMethodSelector.js
// ============================================================================
// PAYMENT METHOD SELECTOR
//
// Radio-card picker used on MerchCheckoutScreen to choose between paying from
// the CritCash wallet vs. via Stripe Checkout (card). Defaults to wallet if
// the user has sufficient combined balance (balance + gift); otherwise the
// wallet option renders disabled with a helpful CTA.
//
// Props:
//   priceCents          - order total in cents
//   walletBalanceCents  - users/{uid}.balanceCents
//   giftBalanceCents    - users/{uid}.giftBalanceCents (drained first)
//   selected            - 'wallet' | 'stripe'
//   onSelect(method)    - parent-controlled selection
//   disabled            - blanket disable (e.g. while submitting)
// ============================================================================

import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const usd = (cents) =>
  `$${((cents || 0) / 100).toFixed(2)}`;

export default function PaymentMethodSelector({
  priceCents,
  walletBalanceCents = 0,
  giftBalanceCents = 0,
  selected,
  onSelect,
  disabled = false,
}) {
  const totalAvailable = (walletBalanceCents || 0) + (giftBalanceCents || 0);
  const walletSufficient = totalAvailable >= (priceCents || 0);
  const walletDisabled = disabled || !walletSufficient;

  const select = (method) => {
    if (disabled) return;
    if (method === 'wallet' && !walletSufficient) return;
    onSelect(method);
  };

  return (
    <View style={styles.wrap}>
      {/* Wallet option */}
      <TouchableOpacity
        accessibilityRole="radio"
        accessibilityState={{ selected: selected === 'wallet', disabled: walletDisabled }}
        onPress={() => select('wallet')}
        disabled={walletDisabled}
        style={[
          styles.card,
          selected === 'wallet' && styles.cardSelected,
          walletDisabled && styles.cardDisabled,
        ]}
      >
        <View style={styles.radioOuter}>
          {selected === 'wallet' && <View style={styles.radioInner} />}
        </View>
        <View style={styles.cardBody}>
          <View style={styles.cardHeader}>
            <Ionicons name="wallet" size={18} color="#FCD000" />
            <Text style={styles.cardTitle}>CritCash Wallet</Text>
          </View>
          <Text style={[styles.cardSub, walletDisabled && styles.cardSubDisabled]}>
            {walletSufficient
              ? `${usd(totalAvailable)} available — pay instantly, no card needed`
              : `Need ${usd((priceCents || 0) - totalAvailable)} more (you have ${usd(totalAvailable)})`}
          </Text>
          {giftBalanceCents > 0 && walletSufficient && (
            <Text style={styles.cardHint}>
              Gift credits ({usd(giftBalanceCents)}) used first.
            </Text>
          )}
        </View>
      </TouchableOpacity>

      {/* Card option (Stripe) */}
      <TouchableOpacity
        accessibilityRole="radio"
        accessibilityState={{ selected: selected === 'stripe', disabled }}
        onPress={() => select('stripe')}
        disabled={disabled}
        style={[
          styles.card,
          selected === 'stripe' && styles.cardSelected,
          disabled && styles.cardDisabled,
        ]}
      >
        <View style={styles.radioOuter}>
          {selected === 'stripe' && <View style={styles.radioInner} />}
        </View>
        <View style={styles.cardBody}>
          <View style={styles.cardHeader}>
            <Ionicons name="card" size={18} color="#3498db" />
            <Text style={styles.cardTitle}>Credit / Debit Card</Text>
          </View>
          <Text style={styles.cardSub}>
            Secure checkout via Stripe. Shipping collected on the next screen.
          </Text>
        </View>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: 10 },
  card: {
    flexDirection: 'row',
    backgroundColor: '#141414',
    borderWidth: 1,
    borderColor: '#262626',
    borderRadius: 10,
    padding: 14,
    gap: 12,
    alignItems: 'flex-start',
  },
  cardSelected: {
    borderColor: '#FCD000',
    backgroundColor: '#1a1a0e',
  },
  cardDisabled: { opacity: 0.55 },
  radioOuter: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: '#666',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  radioInner: {
    width: 9,
    height: 9,
    borderRadius: 5,
    backgroundColor: '#FCD000',
  },
  cardBody: { flex: 1 },
  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 },
  cardTitle: { color: '#fff', fontSize: 15, fontWeight: '700' },
  cardSub: { color: '#bbb', fontSize: 13, lineHeight: 18 },
  cardSubDisabled: { color: '#777' },
  cardHint: { color: '#FCD000', fontSize: 11, marginTop: 4, fontStyle: 'italic' },
});
